import { useEffect, useRef } from 'react';
import { useActiveBundle } from '@/context/active-bundle';
import { AppBundle } from '@/utils/app-bundles';

import { useLocalStorage } from '@/hooks/browser/use-local-storage';

import { Box, SideNav, useSideNavState } from '@customerio/pluma-components/react';
import { shouldShowDevHelper } from '@/layouts/root/components/dev-helper/store';
import { DevToolsNavButton } from './dev-tools-nav-button';
import { TrialCta } from './trial-cta';
import { useNavResize } from './use-nav-resize';
import * as styles from './wrapper.css';

export const NAV_COLLAPSED_LOCAL_STORAGE_KEY = 'hydra:is-main-nav-collapsed';

/**
 * Publishes the collapsed state to the localStorage key embedded apps read on
 * boot. Deliberately not a width writer — `useNavResize` owns the width — and
 * paused mid-drag: previews aren't decisions, and each write is a synchronous
 * setItem broadcast to every other tab.
 */
function useSyncedCollapsedState(isDragging: boolean) {
  const sideNavState = useSideNavState();
  const [, setIsNavCollapsed] = useLocalStorage<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  useEffect(() => {
    if (isDragging) {
      return;
    }
    if (sideNavState != null) {
      setIsNavCollapsed(sideNavState.isCollapsed);
    }
  }, [isDragging, setIsNavCollapsed, sideNavState]);
}

type MainNavigationWrapperProps = {
  isCollapsible?: boolean;
  children?: React.ReactNode;
};

export function MainNavigationWrapper(props: MainNavigationWrapperProps) {
  const { isCollapsible = true, children } = props;

  const activeApp = useActiveBundle();
  const sideNavRef = useRef<HTMLElement>(null);

  const isCollapsed = useSideNavState()?.isCollapsed === true;
  const resize = useNavResize({ isCollapsed, isEnabled: isCollapsible });
  useSyncedCollapsedState(resize.isDragging);

  return (
    <div className={styles.navContainer}>
      <SideNav
        ref={sideNavRef}
        isCollapsible={isCollapsible}
        isCollapsed={!isCollapsible ? false : undefined}
        overflowY="auto"
        overflowX="hidden"
        aria-label="Main"
        style={{ scrollbarWidth: 'thin' }}
        pt="0"
      >
        {children}

        <Box mt="auto">
          <TrialCta />
          {Object.values(AppBundle).map((bundle) => (
            <div
              key={bundle}
              id={`hydra-main-nav-footer-portal-${bundle}`}
              style={activeApp !== bundle ? { display: 'none' } : {}}
            />
          ))}
          {shouldShowDevHelper() ? <DevToolsNavButton sideNavRef={sideNavRef} isCollapsible={isCollapsible} /> : null}
        </Box>
      </SideNav>

      {/*
        Deliberately not a button: the edge is a pointer-only duplicate of
        SideNav's own labelled Collapse/Expand control, so it stays out of the
        accessibility tree. The hint is a native `title` on purpose — a Pluma
        Tooltip anchors to this full-height strip and lands mid-viewport, and
        the OS tooltip follows the cursor without adding Floating UI listeners.
      */}
      {isCollapsible ? (
        <div
          role="presentation"
          title="Select or drag to resize."
          data-test-nav-resize-edge=""
          className={styles.toggleEdge}
          onPointerDown={resize.handlePointerDown}
          onPointerMove={resize.handlePointerMove}
          onPointerUp={resize.endDrag}
          onPointerCancel={resize.endDrag}
        />
      ) : null}
    </div>
  );
}
